import { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';

import { useCartContext } from './CartProvider';
import CartHeader from './BaseComponents/Cart/layout/CartHeader';
import {
  CartBody,
  CartFooter,
  CartContent,
  CartContainer,
} from './BaseComponents/Cart/layout';
import {
  LocationOrderBody,
  LocationOrderFooter,
  LocationProductBody,
  LocationProductFooter,
} from './BaseComponents/Cart/locationFlow';
import CheckoutBody from './BaseComponents/Cart/checkoutFlow/CheckoutBody';
import CheckoutFooter from './BaseComponents/Cart/checkoutFlow/CheckoutFooter';
import { useRouteChange } from 'hooks/useRouteChange';
import Basket from './BaseComponents/Cart/Basket/Basket';
import BasketFooter from './BaseComponents/Cart/Basket/BasketFooter';

const Cart = observer(() => {
  const cart = useCartContext();

  const handleClick = useCallback(() => {
    cart.isOpen ? cart.close() : cart.open();
  }, [cart]);

  const location = useLocation();
  const isAccessServicePage = location.pathname.includes(
    '/order-services/access',
  );
  const isMobilityServicePage = location.pathname.includes(
    '/order-services/mobility',
  );
  const isCheckoutPage = location.pathname.includes('/checkout');
  const isMobilityCheckoutPage = location.pathname.endsWith('/checkout');
  const isAccessProductPage = location.pathname.includes('/access/product');
  const isMobilityProductDetailsPage =
    location.pathname.includes('/product') && !isAccessProductPage;

  useEffect(() => {
    if (isCheckoutPage) {
      cart.open();
    }
  }, [isCheckoutPage, cart]);

  const showOverlay = cart.shippingAssignmentMethod === 'manual' && cart.isOpen;

  useRouteChange(() => {
    cart.resetShippingAssignmentMethod();
  });

  const renderCartBody = () => {
    return isAccessServicePage && cart.cartState === 'locations' ? (
      <LocationOrderBody />
    ) : isCheckoutPage ? (
      <CheckoutBody />
    ) : isAccessProductPage ||
      (isMobilityServicePage && cart.locations.length === 0) ||
      cart.shippingAssignmentMethod === 'manual' ||
      isMobilityProductDetailsPage ||
      !!cart.unassignedPrimaryMobilityItems.length ? (
      <LocationProductBody />
    ) : cart.cartState === 'default' ? (
      <Basket />
    ) : (
      <></>
    );
  };
  const renderCartFooter = () => {
    if (isAccessServicePage && cart.cartState === 'locations') {
      return <LocationOrderFooter />;
    }
    if (
      isAccessProductPage ||
      (isMobilityServicePage && cart.locations.length === 0) ||
      isMobilityProductDetailsPage
    ) {
      return <LocationProductFooter />;
    }
    if (isCheckoutPage) {
      return <CheckoutFooter />;
    }
    if (cart.cartState === 'default') {
      return <BasketFooter />;
    }
    return null;
  };

  return (
    <>
      {showOverlay && (
        <div className="absolute inset-0 z-20 bg-background-base-surface-1/40" />
      )}
      <CartContainer
        isOpen={cart.isOpen}
        onClick={handleClick}
        isElevated={showOverlay}
      >
        <CartHeader
          onIconClick={handleClick}
          isOpen={cart.isOpen}
          totalItems={cart.totalQuantity}
          title={
            (isAccessServicePage && cart.cartState === 'locations') ||
            cart.isEditingLocations ||
            cart.cartState === 'locations'
              ? 'Locations'
              : isCheckoutPage
                ? 'Summary'
                : 'Cart'
          }
          hideActionButton={isCheckoutPage}
        />
        <CartBody isOpen={cart.isOpen}>
          <CartContent
            showScrollbarOnHover={
              isAccessProductPage && !cart.isEditingLocations
            }
          >
            {renderCartBody()}
          </CartContent>
          <CartFooter
            shouldRenderFooter={
              cart.cartState === 'default' && !cart.isEditingLocations
                ? cart.locations.length > 0 ||
                  cart.items.length > 0 ||
                  isMobilityCheckoutPage
                : true
            }
          >
            {renderCartFooter()}
          </CartFooter>
        </CartBody>
      </CartContainer>
    </>
  );
});

export default Cart;
